// src/pages/sow-draft/DraftReviewStep.jsx
//
// Step 3 of the Draft SOW wizard: toolbar (versions/preview/download/
// publish/push), the section list, and the section editor. Split out of
// SowDraftPage.jsx.

import PropTypes from "prop-types";
import { ArrowLeft, Cloud, Download, Eye, History, RefreshCw } from "lucide-react";
import { Button, SurfaceCard } from "../../shared/ui";
import GenerationProgressBar from "./GenerationProgressBar";

export default function DraftReviewStep({
  draft,
  currentVersion,
  orderedSections,
  selectedSectionId,
  onSelectSection,
  selectedSection,
  onContentChange,
  onSave,
  onRegenerate,
  busy,
  generating,
  genProgress,
  onBack,
  onOpenVersions,
  onOpenPreview,
  onExportDocx,
  publishingGdoc,
  onPublishGdoc,
  gdocLink,
  onOpenSalesforce,
}) {
  return (
    <>
      <SurfaceCard className="mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-base font-semibold text-ink flex items-center gap-2">
              {draft.title}
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-brand-50 text-brand-600">
                v{currentVersion}
              </span>
            </h2>
            <p className="text-sm text-muted">Review, edit, and publish your SOW</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="secondary" size="sm" onClick={onBack}>
              <ArrowLeft size={14} /> Back
            </Button>
            <Button variant="secondary" size="sm" onClick={onOpenVersions}>
              <History size={14} /> Versions
            </Button>
            <Button variant="secondary" size="sm" onClick={onOpenPreview} disabled={busy}>
              <Eye size={14} /> Preview
            </Button>
            <Button variant="secondary" size="sm" onClick={onExportDocx} disabled={busy}>
              <Download size={14} /> Download
            </Button>
            <Button variant="secondary" size="sm" onClick={onPublishGdoc} disabled={publishingGdoc} loading={publishingGdoc}>
              Google Docs
            </Button>
            <Button size="sm" onClick={onOpenSalesforce}>
              <Cloud size={14} /> Salesforce
            </Button>
          </div>
        </div>
        {gdocLink && (
          <p className="text-sm mt-3">
            📄{" "}
            <a href={gdocLink} target="_blank" rel="noopener noreferrer" className="text-brand-600 underline">
              {gdocLink}
            </a>
          </p>
        )}
      </SurfaceCard>

      <SurfaceCard className="mb-4">
        <h3 className="text-sm font-semibold text-ink mb-3">Sections ({orderedSections.length})</h3>
        <div className="flex gap-2 flex-wrap">
          {orderedSections.map((s, idx) => {
            const isActive = selectedSectionId === s.section_id;
            return (
              <button
                key={s.section_id}
                type="button"
                onClick={() => onSelectSection(s.section_id)}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                  isActive ? "border-brand-600 bg-brand-50 text-brand-600" : "border-line text-ink hover:bg-gray-50"
                }`}
              >
                <span
                  className={`flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold ${
                    isActive ? "bg-brand-600 text-white" : "bg-gray-100 text-muted"
                  }`}
                >
                  {idx + 1}
                </span>
                {s.title}
                {s.source === "llm" && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">AI</span>
                )}
                {s.source === "user" && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Edited</span>
                )}
                {!s.content && (
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-muted">Empty</span>
                )}
              </button>
            );
          })}
        </div>
      </SurfaceCard>

      <SurfaceCard>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-ink">Edit: {selectedSection?.title || "Select a section"}</h3>
          {selectedSection && (
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={onRegenerate} disabled={generating}>
                <RefreshCw size={14} /> Regenerate
              </Button>
              <Button size="sm" onClick={onSave} disabled={busy}>
                Save
              </Button>
            </div>
          )}
        </div>

        {generating && (
          <GenerationProgressBar
            section={genProgress.section}
            percent={genProgress.percent}
            startedAt={genProgress.startedAt}
            sectionCount={genProgress.total}
          />
        )}

        {selectedSection ? (
          <textarea
            className="shui-input w-full"
            style={{ minHeight: 320 }}
            value={selectedSection.content || ""}
            onChange={(e) => onContentChange(e.target.value)}
            placeholder="Section content…"
          />
        ) : (
          <p className="text-sm text-muted">Select a section above to edit it.</p>
        )}
      </SurfaceCard>
    </>
  );
}

DraftReviewStep.propTypes = {
  draft: PropTypes.object.isRequired,
  currentVersion: PropTypes.string.isRequired,
  orderedSections: PropTypes.array.isRequired,
  selectedSectionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSelectSection: PropTypes.func.isRequired,
  selectedSection: PropTypes.object,
  onContentChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onRegenerate: PropTypes.func.isRequired,
  busy: PropTypes.bool.isRequired,
  generating: PropTypes.bool.isRequired,
  genProgress: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  onOpenVersions: PropTypes.func.isRequired,
  onOpenPreview: PropTypes.func.isRequired,
  onExportDocx: PropTypes.func.isRequired,
  publishingGdoc: PropTypes.bool.isRequired,
  onPublishGdoc: PropTypes.func.isRequired,
  gdocLink: PropTypes.string,
  onOpenSalesforce: PropTypes.func.isRequired,
};
